import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import type { ProactivityCopy } from '@/frontend/features/Fde/ProjectAgentProactivity'
import type { AgentProactivitySettings } from '@/frontend/features/Fde/types'

export default function ProactivityInstructionsDialog({ projectId, assignmentId, agentName, settings, copy, onClose }: {
  projectId: string; assignmentId: string; agentName: string; settings: AgentProactivitySettings
  copy: ProactivityCopy; onClose: () => void
}) {
  // Capture the revision when opening. Background provisioning reloads must not
  // erase the draft or silently authorize overwriting a newer configuration.
  const attention = settings.attention_prompt
  const form = useForm({ revision: settings.revision, proactivity: { proactivity_instructions: attention?.effective ?? settings.proactivity_instructions } })
  const formId = `proactivity-guidance-${assignmentId}`
  const fieldId = `${formId}-text`
  return <FdeEditorDialog open onOpenChange={(open) => { if (!open && !form.processing) onClose() }}
    size={attention ? 'wide' : 'standard'} title={`${attention ? copy.attention_title : copy.instructions_title} · ${agentName}`} closeLabel={copy.close}
    description={attention ? copy.attention_description : copy.instructions_description}
    footer={<>
      <Button type="button" variant="ghost" disabled={form.processing || (attention ? form.data.proactivity.proactivity_instructions === attention.default : !form.data.proactivity.proactivity_instructions)}
        onClick={() => form.setData('proactivity', { proactivity_instructions: attention?.default ?? '' })}>{attention ? copy.attention_reset : copy.instructions_clear}</Button>
      <Button type="button" variant="outline" disabled={form.processing} onClick={onClose}>{copy.cancel}</Button>
      <Button type="submit" form={formId} disabled={form.processing || !form.isDirty}>{form.processing ? copy.saving : copy.save}</Button>
    </>}>
    <div className="mb-5 border-l-2 border-border pl-3">
      <p className="text-sm font-medium">{settings.profile.title}</p>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{settings.profile.description}</p>
    </div>
    <form id={formId} className="space-y-3" onSubmit={(event) => {
      event.preventDefault()
      // This portal lives beneath the project-team form in the React tree.
      // Do not also submit that unrelated parent form.
      event.stopPropagation()
      form.clearErrors()
      form.transform((data) => ({ ...data, proactivity: { proactivity_instructions:
        attention && data.proactivity.proactivity_instructions === attention.default ? '' : data.proactivity.proactivity_instructions } }))
      form.patch(`/dashboard/discovery/agent_proactivities/${assignmentId}?project_id=${projectId}`, {
        preserveScroll: true, onSuccess: onClose,
      })
    }}>
      <label htmlFor={fieldId} className="block text-sm font-medium">{attention ? copy.attention_label : copy.instructions_label}</label>
      <Textarea id={fieldId} value={form.data.proactivity.proactivity_instructions} rows={attention ? 16 : 6}
        disabled={form.processing} maxLength={settings.instructions_limit} placeholder={copy.instructions_placeholder}
        onChange={(event) => form.setData('proactivity', { proactivity_instructions: event.target.value })} />
      {form.errors.proactivity && <p role="alert" className="text-xs text-destructive">{form.errors.proactivity}</p>}
      <p className="text-xs leading-relaxed text-muted-foreground">{attention ? copy.attention_fixed : copy.instructions_fixed}</p>
      {!form.data.proactivity.proactivity_instructions && <p className="text-xs text-muted-foreground">{copy.instructions_empty}</p>}
    </form>
  </FdeEditorDialog>
}
